import { useEffect, useRef, useState } from "react";
import { FaPlus, FaTrash, FaChevronDown, FaChevronRight, FaEllipsisH, FaFlag } from 'react-icons/fa';

import { newPhaseId } from "../../api/sow";

interface SowDraftEditorProps {
  draft: SOWDraft;
  /** Same patch helper the estimator uses, so every edit funnels through one place. */
  patch: (fn: (d: SOWDraft) => SOWDraft) => void;
}

// Keys for rows added in the editor. A phase key is sent back as the phase id,
// so it has to be a real uuid; item keys stay client-only.
let keySeq = 0;
const newKey = (prefix: string) => `${prefix}-new-${keySeq++}`;

const NO_PHASE = '__none__';

// Tailwind 4's preflight sets `cursor: default` on <button>, so every button
// here asks for the pointer back explicitly.
const CHIP_BUTTON =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#555] bg-white border border-[#eaeaea] rounded-md hover:bg-[#fafafa] hover:border-[#ccc] hover:text-[#171717] transition-colors cursor-pointer';

const ICON_BUTTON = 'p-1.5 rounded transition-colors cursor-pointer';

const PRIORITIES: Array<'LOW' | 'MEDIUM' | 'HIGH'> = ['LOW', 'MEDIUM', 'HIGH'];

const PRIORITY_STYLES: Record<'LOW' | 'MEDIUM' | 'HIGH', string> = {
  LOW: 'bg-[#f5f5f5] text-[#777] border-[#eaeaea]',
  MEDIUM: 'bg-blue-50 text-blue-700 border-blue-100',
  HIGH: 'bg-red-50 text-red-700 border-red-100'
};

const titleCase = (p: string) => p[0] + p.slice(1).toLowerCase();

const countLabel = (n: number, singular: string) => `${n} ${singular}${n === 1 ? '' : 's'}`;

/**
 * A line of the draft that reads as text until you click it.
 *
 * The parsed result is reviewed far more often than it is edited, so the rows
 * render as prose and only turn into an input for the field being changed.
 */
const EditableText = ({
  value,
  onCommit,
  label,
  placeholder = '',
  className = '',
  displayClassName = '',
  revealEmpty = false
}: {
  value: string;
  onCommit: (next: string) => void;
  label: string;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
  /** Show the placeholder only on hover/focus of the surrounding row. */
  revealEmpty?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (local !== value) onCommit(local);
  };

  const cancel = () => {
    setLocal(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        aria-label={label}
        value={local}
        placeholder={placeholder}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        className={`w-full px-1.5 py-0.5 -mx-1.5 bg-white border border-[#888] rounded outline-none ${className}`}
      />
    );
  }

  const empty = !value.trim();
  return (
    <button
      type="button"
      title="Click to edit"
      onClick={() => { setLocal(value); setEditing(true); }}
      className={`w-full text-left px-1.5 py-0.5 -mx-1.5 rounded border border-transparent hover:bg-[#f5f5f5] hover:border-[#eaeaea] transition-colors cursor-text ${className} ${displayClassName} ${
        empty && revealEmpty ? 'hidden group-hover:block group-focus-within:block' : ''
      }`}
    >
      {empty ? <span className="text-[#bbb]">{placeholder}</span> : value}
    </button>
  );
};

/** Per-row overflow menu: move between phases, delete. Keeps the rows uncluttered. */
const RowMenu = ({
  label,
  phases,
  currentPhaseKey,
  onMove,
  onRemove,
  removeLabel
}: {
  label: string;
  phases: SOWDraftPhase[];
  currentPhaseKey: string | null;
  onMove: (phaseKey: string | null) => void;
  onRemove: () => void;
  removeLabel: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`${ICON_BUTTON} border ${
          open
            ? 'text-[#171717] bg-[#f0f0f0] border-[#ddd]'
            : 'text-[#888] border-transparent hover:text-[#171717] hover:bg-[#f5f5f5] hover:border-[#e5e5e5]'
        }`}
      >
        <FaEllipsisH className="w-3 h-3" />
      </button>

      {open && (
        <>
          {/* Click-away catcher */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 w-56 py-1 bg-white border border-[#eaeaea] rounded-lg shadow-lg">
            <p className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-[#bbb]">Move to phase</p>
            {phases.map((p, i) => (
              <button
                key={p.key}
                type="button"
                onClick={() => { onMove(p.key); setOpen(false); }}
                disabled={p.key === currentPhaseKey}
                className="w-full px-3 py-1.5 text-left text-xs text-[#555] hover:bg-[#fafafa] cursor-pointer disabled:text-[#bbb] disabled:hover:bg-transparent disabled:cursor-default truncate"
              >
                {i + 1}. {p.name || 'Untitled phase'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { onMove(null); setOpen(false); }}
              disabled={currentPhaseKey === null}
              className="w-full px-3 py-1.5 text-left text-xs text-[#555] hover:bg-[#fafafa] cursor-pointer disabled:text-[#bbb] disabled:hover:bg-transparent disabled:cursor-default"
            >
              No phase
            </button>
            <div className="my-1 border-t border-[#eaeaea]" />
            <button
              type="button"
              onClick={() => { onRemove(); setOpen(false); }}
              className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 cursor-pointer flex items-center gap-2"
            >
              <FaTrash className="w-2.5 h-2.5" /> {removeLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export const SowDraftEditor = ({ draft, patch }: SowDraftEditorProps) => {
  // Collapsed phases, by key. Everything starts open; the header toggles all.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const groupKeys: Array<string | null> = [...draft.phases.map(p => p.key), null];
  const allCollapsed = groupKeys.every(k => collapsed.has(k ?? NO_PHASE));

  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(groupKeys.map(k => k ?? NO_PHASE)));

  // ---- mutations ----------------------------------------------------------
  const addPhase = () =>
    patch(d => ({ ...d, phases: [...d.phases, { key: newPhaseId(), name: 'New phase', description: '' }] }));

  const updatePhase = (key: string, field: 'name' | 'description', value: string) =>
    patch(d => ({ ...d, phases: d.phases.map(p => (p.key === key ? { ...p, [field]: value } : p)) }));

  const removePhase = (key: string) =>
    patch(d => ({
      ...d,
      phases: d.phases.filter(p => p.key !== key),
      // Orphan this phase's tasks rather than deleting them.
      items: d.items.map(i => (i.phaseKey === key ? { ...i, phaseKey: null } : i)),
      actionItems: d.actionItems.map(a => (a.phaseKey === key ? { ...a, phaseKey: null } : a))
    }));

  const addItem = (phaseKey: string | null) =>
    patch(d => ({
      ...d,
      items: [...d.items, { key: newKey('item'), phaseKey, name: 'New task', description: '', priority: 'MEDIUM' }]
    }));

  const updateItem = (key: string, field: 'name' | 'description', value: string) =>
    patch(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...i, [field]: value } : i)) }));

  const setItemPriority = (key: string, priority: 'LOW' | 'MEDIUM' | 'HIGH') =>
    patch(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...i, priority } : i)) }));

  const moveItem = (key: string, phaseKey: string | null) =>
    patch(d => ({ ...d, items: d.items.map(i => (i.key === key ? { ...i, phaseKey } : i)) }));

  const removeItem = (key: string) =>
    patch(d => ({ ...d, items: d.items.filter(i => i.key !== key) }));

  const addAction = (phaseKey: string | null) =>
    patch(d => ({
      ...d,
      actionItems: [...d.actionItems, { key: newKey('action'), phaseKey, name: 'New action item', description: '' }]
    }));

  const updateAction = (key: string, field: 'name' | 'description', value: string) =>
    patch(d => ({ ...d, actionItems: d.actionItems.map(a => (a.key === key ? { ...a, [field]: value } : a)) }));

  const moveAction = (key: string, phaseKey: string | null) =>
    patch(d => ({ ...d, actionItems: d.actionItems.map(a => (a.key === key ? { ...a, phaseKey } : a)) }));

  const removeAction = (key: string) =>
    patch(d => ({ ...d, actionItems: d.actionItems.filter(a => a.key !== key) }));

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[#171717]">Phases &amp; tasks</h4>
          <p className="mt-0.5 text-xs text-[#888]">
            {countLabel(draft.phases.length, 'phase')} · {countLabel(draft.items.length, 'task')} ·{' '}
            {countLabel(draft.actionItems.length, 'action item')} · click any line to edit
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={toggleAll} className={CHIP_BUTTON}>
            {allCollapsed ? <FaChevronDown className="w-2.5 h-2.5" /> : <FaChevronRight className="w-2.5 h-2.5" />}
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
          <button type="button" onClick={addPhase} className={`${CHIP_BUTTON} text-[#171717] border-[#ddd]`}>
            <FaPlus className="w-2.5 h-2.5" /> Add phase
          </button>
        </div>
      </div>

      {groupKeys.map((groupKey, groupIndex) => {
        const phase = groupKey ? draft.phases.find(p => p.key === groupKey) : null;
        const groupItems = draft.items.filter(i => i.phaseKey === groupKey);
        const groupActions = draft.actionItems.filter(a => a.phaseKey === groupKey);
        if (groupKey === null && groupItems.length === 0 && groupActions.length === 0) return null;

        const stateKey = groupKey ?? NO_PHASE;
        const isOpen = !collapsed.has(stateKey);
        const rowCount = groupItems.length + groupActions.length;

        return (
          <div
            key={stateKey}
            // No overflow-hidden: a row's ⋯ menu has to escape the card.
            className={`rounded-lg ${phase ? 'border border-[#eaeaea]' : 'border border-dashed border-[#ddd]'}`}
          >
            {/* Phase header */}
            <div className={`group flex items-start gap-3 px-3 py-2.5 rounded-t-lg ${isOpen ? '' : 'rounded-b-lg'} ${phase ? 'bg-[#fafafa]' : 'bg-white'}`}>
              <button
                type="button"
                aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${phase ? phase.name || 'phase' : 'unassigned tasks'}`}
                aria-expanded={isOpen}
                onClick={() => toggle(stateKey)}
                className="mt-0.5 p-1 -m-1 text-[#aaa] hover:text-[#555] cursor-pointer"
              >
                {isOpen ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
              </button>

              {phase ? (
                <>
                  <span className="mt-px shrink-0 w-5 h-5 rounded-full bg-white border border-[#e0e0e0] text-[11px] font-semibold text-[#555] flex items-center justify-center">
                    {groupIndex + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <EditableText
                      value={phase.name}
                      onCommit={v => updatePhase(phase.key, 'name', v)}
                      label={`Phase name: ${phase.name}`}
                      placeholder="Untitled phase"
                      className="text-sm"
                      displayClassName="font-semibold text-[#171717] hover:bg-white"
                    />
                    <EditableText
                      value={phase.description}
                      onCommit={v => updatePhase(phase.key, 'description', v)}
                      label={`Phase description: ${phase.name}`}
                      placeholder="Add a description"
                      className="text-xs"
                      displayClassName="text-[#888] hover:bg-white"
                      revealEmpty
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#888]">No phase</p>
                  <p className="text-xs text-[#aaa]">Rows the parser could not place — move them from the ⋯ menu.</p>
                </div>
              )}

              <span className="mt-1 shrink-0 text-xs text-[#aaa] whitespace-nowrap">
                {countLabel(groupItems.length, 'task')}
                {groupActions.length > 0 && ` · ${countLabel(groupActions.length, 'action')}`}
              </span>

              {phase && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    aria-label={`Remove phase ${phase.name}`}
                    title="Remove phase (keeps its tasks)"
                    onClick={() => removePhase(phase.key)}
                    className={`${ICON_BUTTON} text-[#aaa] hover:text-red-600 hover:bg-red-50`}
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            {isOpen && (
              <div className="bg-white rounded-b-lg divide-y divide-[#f5f5f5]">
                {rowCount === 0 && (
                  <p className="px-4 py-3 text-xs text-[#bbb]">No tasks in this phase yet.</p>
                )}

                {groupItems.map(item => (
                  <div key={item.key} className="group flex items-start gap-3 px-4 py-2.5 hover:bg-[#fcfcfc]">
                    <div className="flex-1 min-w-0">
                      <EditableText
                        value={item.name}
                        onCommit={v => updateItem(item.key, 'name', v)}
                        label={`Task name: ${item.name}`}
                        placeholder="Untitled task"
                        className="text-sm"
                        displayClassName="text-[#171717]"
                      />
                      <EditableText
                        value={item.description}
                        onCommit={v => updateItem(item.key, 'description', v)}
                        label={`Task description: ${item.name}`}
                        placeholder="Add a description"
                        className="text-xs"
                        displayClassName="text-[#888]"
                        revealEmpty
                      />
                    </div>

                    <select
                      aria-label={`Priority for ${item.name}`}
                      value={item.priority}
                      onChange={e => setItemPriority(item.key, e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
                      className={`mt-0.5 shrink-0 appearance-none text-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded-full cursor-pointer focus:outline-none focus:border-[#888] ${PRIORITY_STYLES[item.priority]}`}
                    >
                      {PRIORITIES.map(p => <option key={p} value={p}>{titleCase(p)}</option>)}
                    </select>

                    <RowMenu
                      label={`Options for ${item.name}`}
                      phases={draft.phases}
                      currentPhaseKey={item.phaseKey}
                      onMove={phaseKey => moveItem(item.key, phaseKey)}
                      onRemove={() => removeItem(item.key)}
                      removeLabel="Delete task"
                    />
                  </div>
                ))}

                {groupActions.map(ai => (
                  <div key={ai.key} className="group flex items-start gap-3 px-4 py-2.5 hover:bg-[#fcfcfc]">
                    <FaFlag className="mt-1.5 w-2.5 h-2.5 shrink-0 text-amber-500" title="Action item" />
                    <div className="flex-1 min-w-0">
                      <EditableText
                        value={ai.name}
                        onCommit={v => updateAction(ai.key, 'name', v)}
                        label={`Action item name: ${ai.name}`}
                        placeholder="Untitled action item"
                        className="text-sm"
                        displayClassName="text-[#171717]"
                      />
                      <EditableText
                        value={ai.description}
                        onCommit={v => updateAction(ai.key, 'description', v)}
                        label={`Action item description: ${ai.name}`}
                        placeholder="Add a description"
                        className="text-xs"
                        displayClassName="text-[#888]"
                        revealEmpty
                      />
                    </div>

                    <span className="mt-0.5 shrink-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-100 rounded-full">
                      Action
                    </span>

                    <RowMenu
                      label={`Options for ${ai.name}`}
                      phases={draft.phases}
                      currentPhaseKey={ai.phaseKey}
                      onMove={phaseKey => moveAction(ai.key, phaseKey)}
                      onRemove={() => removeAction(ai.key)}
                      removeLabel="Delete action item"
                    />
                  </div>
                ))}

                <div className="flex items-center gap-2 px-4 py-2.5">
                  <button type="button" onClick={() => addItem(groupKey)} className={CHIP_BUTTON}>
                    <FaPlus className="w-2.5 h-2.5" /> Task
                  </button>
                  <button type="button" onClick={() => addAction(groupKey)} className={CHIP_BUTTON}>
                    <FaPlus className="w-2.5 h-2.5" /> Action item
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default SowDraftEditor;
